import { type VariantProps, cva } from 'class-variance-authority';
import {
    type KeyboardEvent,
    type ReactNode,
    useEffect,
    useRef,
    useState,
    type JSX,
} from 'react';

import type { CommonProps } from '../../types';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { ButtonOrLink } from '../button-or-link';

export interface InputFieldProps
    extends CommonProps,
        VariantProps<typeof InputFieldStyles> {
    placeholder?: string;
    type?: 'email' | 'search' | 'text' | 'date';
    icon?: ReactNode;
    name?: string;
    value?: string;
    label?: string;
    autoFocus?: boolean;
    clearOnSubmit?: boolean;
    disabled?: boolean;
    required?: boolean;
    pattern?: string;
    hideLabel?: boolean;
    max?: string;
    maxLength?: number;
    readOnly?: boolean;
    onSubmit?: (value: string, language: string) => void;
    onChange?: (value: string, type: string, placeholder: string) => void;
    onInput?: (value: string) => void;
    onFocus?: () => void;
}

export const InputFieldStyles = cva('relative w-full');

export const InputField = ({
    placeholder,
    type,
    icon,
    name,
    value,
    label,
    disabled,
    required,
    pattern,
    hideLabel,
    max,
    maxLength,
    autoFocus = false,
    clearOnSubmit = false,
    className,
    onSubmit,
    onChange,
    onInput,
    onFocus,
    testId = 'input-field',
    readOnly,
}: InputFieldProps): JSX.Element => {
    const [inputValue, setInputValue] = useState(value || '');
    const inputRef = useRef<HTMLInputElement>(null);
    const { i18n } = useTranslation();

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const handleKeyPress = (e: KeyboardEvent<Element>): void => {
        if (e.key === 'Enter') {
            e.preventDefault();

            handleSubmit();
        }
    };

    const handleSubmit = (): void => {
        if (onSubmit) {
            onSubmit(inputValue, i18n.language);

            if (clearOnSubmit) {
                setInputValue('');
            }
        }
    };

    return (
        <div className={twMerge(InputFieldStyles({ className }))}>
            {label && (
                <label
                    className={`${
                        hideLabel ? 'sr-only' : ''
                    } mb-2 pl-5 opacity-70`}
                    htmlFor={name}
                >
                    {label} {required && '*'}
                </label>
            )}
            <input
                ref={inputRef}
                aria-label={name}
                id={name}
                autoComplete="on"
                className={`focus w-full rounded-full border border-gray-100 bg-transparent py-[9px] pr-4 indent-3 text-white placeholder-gray-100 focus-visible:rounded-full disabled:bg-gray-300 disabled:text-gray-100 ${
                    type === 'text' || type === 'email' ? 'pl-1.5' : 'pl-0'
                }`}
                name={name}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                pattern={pattern}
                type={type}
                defaultValue={inputValue}
                max={max}
                maxLength={maxLength}
                readOnly={readOnly}
                onChange={(e) =>
                    onChange
                        ? onChange(
                              e.target.value,
                              e.target.type,
                              e.target.placeholder
                          )
                        : setInputValue(e.target.value)
                }
                onInput={(e) =>
                    onInput
                        ? onInput((e.target as HTMLInputElement).value)
                        : setInputValue((e.target as HTMLInputElement).value)
                }
                onKeyDown={handleKeyPress}
                onFocus={onFocus}
                data-testid={testId}
            />
            {icon && (
                <ButtonOrLink
                    aria-label="submit"
                    className="focus absolute bottom-0 right-0 rounded-r-xl border border-white bg-transparent px-3.5 py-[9px] transition-colors focus-visible:rounded-r-xl lg:hover:bg-white lg:hover:text-black"
                    onClick={handleSubmit}
                >
                    {icon}
                </ButtonOrLink>
            )}
        </div>
    );
};
